import { cn } from './utils';

export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export type StatusToneLike = StatusTone | 'error';

type StatusToneSlots = {
  /** Primary text color for the tone */
  text: string;
  /** Strong text color for emphasis (defaults to text tone when palette is limited) */
  emphasis: string;
  /** Muted text color for supporting copy */
  muted: string;
  /** Icon color */
  icon: string;
  /** Background color for subtle badges/pills */
  subtleBackground: string;
  /** Border color for subtle badges/pills */
  subtleBorder: string;
  /** Text color when used on subtle backgrounds */
  subtleText: string;
  /** Background color for surfaces (e.g., alerts, toasts) */
  surfaceBackground: string;
  /** Border color for surfaces */
  surfaceBorder: string;
  /** Focus ring color */
  ring: string;
  /** Background color for solid elements (e.g., destructive buttons) */
  solidBackground: string;
  /** Text color for solid elements */
  solidText: string;
};

const STATUS_TONE_MAP: Record<StatusTone, StatusToneSlots> = {
  neutral: {
    text: 'text-brand-blue-500',
    emphasis: 'text-brand-blue-700',
    muted: 'text-brand-blue-400',
    icon: 'text-brand-blue-600',
    subtleBackground: 'bg-brand-blue-50',
    subtleBorder: 'border-brand-blue-200',
    subtleText: 'text-brand-blue-600',
    surfaceBackground: 'bg-brand-blue-50',
    surfaceBorder: 'border-brand-blue-200',
    ring: 'focus:ring-brand-blue-500/30',
    solidBackground: 'bg-brand-blue-600',
    solidText: 'text-base-white',
  },
  info: {
    text: 'text-brand-blue-600',
    emphasis: 'text-brand-blue-700',
    muted: 'text-brand-blue-500',
    icon: 'text-brand-blue-600',
    subtleBackground: 'bg-brand-blue-100',
    subtleBorder: 'border-brand-blue-400',
    subtleText: 'text-brand-blue-700',
    surfaceBackground: 'bg-brand-blue-50',
    surfaceBorder: 'border-brand-blue-400',
    ring: 'focus:ring-brand-blue-500/30',
    solidBackground: 'bg-brand-blue-600',
    solidText: 'text-base-white',
  },
  success: {
    text: 'text-success-500',
    emphasis: 'text-success-500',
    muted: 'text-success-500/80',
    icon: 'text-success-500',
    subtleBackground: 'bg-success-100',
    subtleBorder: 'border-success-500',
    subtleText: 'text-success-500',
    surfaceBackground: 'bg-success-100',
    surfaceBorder: 'border-success-500/30',
    ring: 'focus:ring-success-500/30',
    solidBackground: 'bg-success-500',
    solidText: 'text-base-white',
  },
  warning: {
    text: 'text-brand-blue-700',
    emphasis: 'text-brand-blue-800',
    muted: 'text-brand-blue-500',
    icon: 'text-brand-blue-700',
    subtleBackground: 'bg-warning-100',
    subtleBorder: 'border-warning-500',
    subtleText: 'text-brand-blue-800',
    surfaceBackground: 'bg-warning-100',
    surfaceBorder: 'border-warning-500/30',
    ring: 'focus:ring-warning-500/30',
    solidBackground: 'bg-warning-500',
    solidText: 'text-brand-blue-900',
  },
  danger: {
    text: 'text-error-700',
    emphasis: 'text-error-800',
    muted: 'text-error-600',
    icon: 'text-error-700',
    subtleBackground: 'bg-error-100',
    subtleBorder: 'border-error-500',
    subtleText: 'text-error-800',
    surfaceBackground: 'bg-error-100',
    surfaceBorder: 'border-error-500/30',
    ring: 'focus:ring-error-500/30',
    solidBackground: 'bg-error-500',
    solidText: 'text-base-white',
  },
};

function resolveTone(tone: StatusToneLike): StatusTone {
  if (tone === 'error') return 'danger';
  return tone;
}

export function statusToneClasses(tone: StatusToneLike): StatusToneSlots {
  return STATUS_TONE_MAP[resolveTone(tone)];
}

export function statusToneSubtleClass(
  tone: StatusToneLike,
  options: { withBorder?: boolean } = {}
): string {
  const tokens = statusToneClasses(tone);
  return cn(
    tokens.subtleBackground,
    tokens.subtleText,
    options.withBorder ? ['border', tokens.subtleBorder] : tokens.subtleBorder
  );
}

export function statusToneSurfaceClass(
  tone: StatusToneLike,
  options: { withBorder?: boolean } = {}
): string {
  const tokens = statusToneClasses(tone);
  return cn(
    tokens.surfaceBackground,
    tokens.text,
    options.withBorder ? ['border', tokens.surfaceBorder] : tokens.surfaceBorder
  );
}

export function statusToneTextClass(tone: StatusToneLike): string {
  return statusToneClasses(tone).text;
}

export function statusToneIconClass(tone: StatusToneLike): string {
  return statusToneClasses(tone).icon;
}

function statusToneRingClass(tone: StatusToneLike): string {
  return statusToneClasses(tone).ring;
}

function statusToneBorderClass(tone: StatusToneLike, options?: { subtle?: boolean }): string {
  const tokens = statusToneClasses(tone);
  return options?.subtle ? tokens.surfaceBorder : tokens.subtleBorder;
}

export function statusToneSolidClass(tone: StatusToneLike): {
  background: string;
  text: string;
} {
  const tokens = statusToneClasses(tone);
  return {
    background: tokens.solidBackground,
    text: tokens.solidText,
  };
}

export function statusToneInteractiveFieldClasses(tone: StatusToneLike): string[] {
  const borderClass = statusToneBorderClass(tone);
  const borderToken = borderClass.replace(/^border-/, '');
  return [
    borderClass,
    `hover:border-${borderToken}`,
    `focus:border-${borderToken}`,
    statusToneRingClass(tone),
  ];
}
